import { ft } from '../core/units.js';
import { parseTime } from './clock.js';
import { SHUTTLES } from '../data/shuttles.js';

/**
 * Guests who have to be somewhere by a time, and the golf cart.
 *
 * Posting one does the only authored things there are: the guests come out
 * and wait where the row says, and the clock is running. They are guests like
 * any other the rest of the time, and they are not on the property at all
 * until somebody asks for them to be driven, which is why they are held back
 * until then.
 *
 *   the lift       `riders.js`: stop by them, and the key offers them a seat
 *   the road       the bends, the branches and the snake, which are the road's
 *   the complaint  `witnesses.js`: a guest who was late says so to Management,
 *                  as guests do
 *
 * This file watches where they are let out, sends them to their table when it
 * is the right place, and keeps the score: everybody there on time, there
 * late, or never got there.
 */

const clockOf = (clock) => clock.day * 1440 + clock.minutes;

export function createShuttles({ npcs, level, clock, standing, hud, complain = () => {}, rows = SHUTTLES }) {
  let active = null;
  let records = [];

  const rowOf = (id) => rows.find((row) => row.id === id) ?? null;
  const guestsFor = (row) => npcs.filter((npc) => npc.data.shuttle === row.id);
  const roomOf = (npc) => level.roomAt(npc.position.x, npc.position.z)?.id ?? null;
  const dueOf = (row, day) => day * 1440 + parseTime(row.due);

  /**
   * Off the property, every one of them, until a shuttle is on, and back off
   * it the day after: the evening it ran, they are at their dinner until their
   * hours are over.
   */
  function holdAll() {
    for (const row of rows) {
      if (active?.id === row.id) continue;
      if (records.some((record) => record.id === row.id && record.day === clock.day)) continue;
      for (const npc of guestsFor(row)) {
        if (npc.posted) npc.unpost({ home: false });
        npc.holdBack(true);
      }
    }
  }
  holdAll();

  function close(outcome) {
    const row = rowOf(active.id);
    records.push({ id: active.id, day: active.day, outcome, at: clock.minutes, arrived: { ...active.arrived } });
    const changes = outcome === 'on-time' ? row.award : outcome === 'late' ? row.late : row.missed;
    const reason =
      outcome === 'on-time' ? 'Got the guests to dinner on time' : outcome === 'late' ? 'The guests were late for dinner' : 'The guests never got to dinner';
    standing?.applyAll(changes ?? {}, { reason });
    if (outcome !== 'on-time') {
      for (const npc of guestsFor(row)) {
        complain({ npc, record: { what: 'late', detail: row.title.toLowerCase(), subject: 'player' } });
      }
    }
    hud?.say(
      outcome === 'on-time' ? 'They made it, with time for a drink.' : outcome === 'late' ? 'They made it. Late.' : 'Somebody else drove them in the end.',
      { tone: outcome === 'on-time' ? 'good' : 'warn', seconds: 4 }
    );
    active = null;
  }

  return {
    rows,

    /** Somebody wants driving. From a beat, when it is played. */
    post(id) {
      const row = rowOf(id);
      if (!row || active) return false;
      const guests = guestsFor(row);
      if (!guests.length) return false;
      active = { id, day: clock.day, from: clockOf(clock), arrived: {} };
      guests.forEach((npc, index) => {
        npc.holdBack(false);
        const [x, z] = row.waits[index % row.waits.length];
        npc.post([{ x: ft(x), z: ft(z), dwell: 1e6 }], { from: true });
      });
      return true;
    },

    get active() {
      return active ? { ...active, arrived: { ...active.arrived } } : null;
    },

    get records() {
      return records.map((record) => ({ ...record, arrived: { ...record.arrived } }));
    },

    /** Zones the job you are on puts you in. */
    works(zone) {
      return Boolean(active && rowOf(active.id)?.works.includes(zone));
    },

    /** Where the guests are going, in feet, for the objective and the map. */
    get destination() {
      if (!active) return null;
      const row = rowOf(active.id);
      const seats = row.to.seats;
      const x = seats.reduce((sum, seat) => sum + seat[0], 0) / seats.length;
      const z = seats.reduce((sum, seat) => sum + seat[1], 0) / seats.length;
      return { room: row.to.room, x, z };
    },

    update() {
      if (!active) {
        holdAll();
        return;
      }
      const row = rowOf(active.id);
      const guests = guestsFor(row);
      guests.forEach((npc, index) => {
        if (active.arrived[npc.data.id] !== undefined || npc.aboard) return;
        // Let out where they were going: to the table, and not back up the road.
        if (roomOf(npc) === row.to.room) {
          active.arrived[npc.data.id] = clock.minutes;
          const [x, z] = row.to.seats[index % row.to.seats.length];
          npc.post([{ x: ft(x), z: ft(z), dwell: 1e6 }], { now: true });
        }
      });
      const due = dueOf(row, active.day);
      if (guests.every((npc) => active.arrived[npc.data.id] !== undefined)) {
        const latest = Math.max(...guests.map((npc) => active.day * 1440 + active.arrived[npc.data.id]));
        return close(latest <= due ? 'on-time' : 'late');
      }
      if (clockOf(clock) >= due + row.givesUp && !guests.some((npc) => npc.aboard)) return close('missed');
    },

    toJSON: () => ({ active, records }),
    load(state) {
      active = state?.active ? { ...state.active, arrived: { ...(state.active.arrived ?? {}) } } : null;
      records = (state?.records ?? []).map((record) => ({ ...record }));
      if (active) {
        const row = rowOf(active.id);
        if (!row) active = null;
        else {
          // Back where they were waiting, the way a reload puts everybody at a post.
          guestsFor(row).forEach((npc, index) => {
            npc.holdBack(false);
            const arrived = active.arrived[npc.data.id] !== undefined;
            const [x, z] = arrived ? row.to.seats[index % row.to.seats.length] : row.waits[index % row.waits.length];
            npc.post([{ x: ft(x), z: ft(z), dwell: 1e6 }], { from: true });
          });
        }
      }
      holdAll();
    },
    clear() {
      active = null;
      records = [];
      holdAll();
    },
  };
}
